import React, { useState } from 'react';
import {
  CheckCircleIcon as CheckCircleSolid,
} from '@heroicons/react/24/solid';
import {
  XCircleIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  AdjustmentsHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';

const AI_ENGINES = [
  { key: 'chatgpt', label: 'ChatGPT' },
  { key: 'perplexity', label: 'Perplexity' },
  { key: 'googleAI', label: 'Google AI' },
  { key: 'copilot', label: 'Copilot' },
];

const DATE_OPTIONS = ['Last 7 days', 'Last 30 days', 'Last 90 days', 'All time'];
const ROWS_PER_PAGE = 5;

const RANK_COLORS = {
  up: 'text-green-600',
  down: 'text-red-500',
  same: 'text-gray-500',
};

const SOURCE_COLORS = {
  Ahrefs: 'bg-orange-100 text-orange-700',
  SEMrush: 'bg-purple-100 text-purple-700',
  Moz: 'bg-yellow-100 text-yellow-700',
  Copilot: 'bg-blue-100 text-blue-700',
  ChatGPT: 'bg-green-100 text-green-700',
  Perplexity: 'bg-sky-100 text-sky-700',
};

const AIAnswerSnapshot = ({ data = [], loading = false }) => {
  const [dateFrom, setDateFrom] = useState('Last 30 days');
  const [dateTo, setDateTo] = useState('Last 30 days');
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(data.length / ROWS_PER_PAGE));
  const startIdx = (currentPage - 1) * ROWS_PER_PAGE;
  const pageData = data.slice(startIdx, startIdx + ROWS_PER_PAGE);

  const renderEngineStatus = (isPresent) => {
    if (isPresent) {
      return (
        <CheckCircleSolid className="h-5 w-5 text-green-500 mx-auto" aria-label="Present in answer" />
      );
    }
    return (
      <XCircleIcon className="h-5 w-5 text-gray-300 mx-auto" aria-label="Not present in answer" />
    );
  };

  const renderRank = (rank) => {
    if (!rank) return <span className="text-gray-400"></span>;

    return (
      <div className="flex items-center justify-center gap-1.5">
        {rank.change && rank.change !== 0 && (
          <span className={`flex items-center text-xs font-semibold ${rank.change > 0 ? RANK_COLORS.up : RANK_COLORS.down}`}>
            {rank.change > 0 ? (
              <ArrowUpIcon className="h-3 w-3 mr-0.5" />
            ) : (
              <ArrowDownIcon className="h-3 w-3 mr-0.5" />
            )}
            {Math.abs(rank.change)}
          </span>
        )}
        {rank.position && (
          <span className="text-sm font-semibold text-gray-800">{rank.position}</span>
        )}
        {rank.source && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${SOURCE_COLORS[rank.source] || 'bg-gray-100 text-gray-600'}`}>
            {rank.source}
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6" role="status" aria-label="Loading answer snapshot data">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-6 bg-gray-200 rounded w-44"></div>
            <div className="flex gap-2">
              <div className="h-9 bg-gray-200 rounded w-32"></div>
              <div className="h-9 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded w-full"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <h3 className="text-lg font-semibold text-gray-900">AI Answer Snapshot</h3>
        <div className="flex items-center gap-2">
          <select
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Snapshot date from"
          >
            {DATE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <select
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Snapshot date to"
          >
            {DATE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <button
            type="button"
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="View trend analysis"
          >
            <ArrowTrendingUpIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="View snapshot history"
          >
            <ClockIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full" role="table" aria-label="AI Answer Snapshot comparison data">
          <thead>
            <tr className="border-b border-gray-200">
              <th scope="col" className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 pr-4 min-w-[180px]">
                Prompt
              </th>
              {AI_ENGINES.map((engine) => (
                <th key={engine.key} scope="col" className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-2 w-20">
                  {engine.label}
                </th>
              ))}
              <th scope="col" className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 pl-2 w-32">
                Rank
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageData.length > 0 ? (
              pageData.map((row, idx) => (
                <tr key={row.id || idx} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3.5 pr-4 text-sm font-medium text-gray-900">
                    {row.prompt}
                  </td>
                  {AI_ENGINES.map((engine) => (
                    <td key={engine.key} className="py-3.5 px-2 text-center">
                      {renderEngineStatus(row[engine.key])}
                    </td>
                  ))}
                  <td className="py-3.5 pl-2 text-center">
                    {renderRank(row.rank)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="py-16 text-center">
                  <AdjustmentsHorizontalIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-500">No snapshot data available yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Snapshots will appear after your first AI engine scan.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.length > ROWS_PER_PAGE && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Showing {startIdx + 1}{Math.min(startIdx + ROWS_PER_PAGE, data.length)} of {data.length} snapshots
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                type="button"
                onClick={() => setCurrentPage(i + 1)}
                className={`min-w-[32px] h-8 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === i + 1
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-label={`Page ${i + 1}`}
                aria-current={currentPage === i + 1 ? 'page' : undefined}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAnswerSnapshot;
